import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import type { SearchableSelectOption } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import type { DraftSubtask } from "@/services/inbound-email.service";
import { textareaClass } from "@/components/workspace-settings/email-to-task/fields";
import { DraftIssues } from "./DraftIssues";

export function DraftSubtaskRow({
	subtask,
	userOptions,
	editable,
	parentIncluded,
	onChange,
}: {
	subtask: DraftSubtask;
	userOptions: SearchableSelectOption[];
	editable: boolean;
	parentIncluded: boolean;
	onChange: (patch: Partial<DraftSubtask>) => void;
}) {
	const disabled = !editable || !parentIncluded;
	const muted = !subtask.include || !parentIncluded;

	return (
		<li className={cn("rounded-lg border border-border bg-background p-3", muted && "opacity-60")}>
			<div className="flex items-start gap-3">
				<Checkbox
					className="mt-2.5"
					checked={subtask.include}
					onCheckedChange={(v) => onChange({ include: v === true })}
					disabled={disabled}
					aria-label={`Create subtask ${subtask.title}`}
				/>
				<div className="min-w-0 flex-1 space-y-2">
					<div className="grid gap-2 md:grid-cols-[1fr_220px_150px]">
						<Input
							value={subtask.title}
							onChange={(e) => onChange({ title: e.target.value })}
							disabled={disabled || !subtask.include}
							aria-label="Subtask title"
						/>
						<MultiSelect
							value={subtask.assignee_ids}
							onValueChange={(ids) => onChange({ assignee_ids: ids })}
							options={userOptions}
							placeholder="Unassigned"
							size="sm"
							disabled={disabled || !subtask.include}
							ariaLabel="Subtask assignees"
						/>
						<Input
							type="date"
							value={subtask.due_date ?? ""}
							onChange={(e) => onChange({ due_date: e.target.value || null })}
							disabled={disabled || !subtask.include}
							aria-label="Subtask due date"
						/>
					</div>
					{(subtask.checklist.length > 0 || (editable && subtask.include)) && (
						<textarea
							rows={Math.min(6, Math.max(2, subtask.checklist.length))}
							className={cn(textareaClass, "text-xs")}
							placeholder="Checklist, one item per line"
							value={subtask.checklist.join("\n")}
							onChange={(e) => onChange({ checklist: e.target.value.split("\n") })}
							onBlur={() => onChange({ checklist: subtask.checklist.map((c) => c.trim()).filter(Boolean) })}
							disabled={disabled || !subtask.include}
							aria-label="Checklist items"
						/>
					)}
					<DraftIssues unresolved={subtask.unresolved_names} warnings={subtask.warnings} />
				</div>
			</div>
		</li>
	);
}
